import React from 'react';
import { Link } from 'react-router-dom';

import ActionLink from '../components/admin/ActionLink';
import PageIntro from '../components/admin/PageIntro';
import Pill from '../components/admin/Pill';
import SectionCard from '../components/admin/SectionCard';
import { adminTheme } from '../components/admin/adminTheme';

const modules = [
  {
    title: 'Providers',
    description:
      'Browse provider records and verify backend-connected listing flows.',
    to: '/staff/dashboard/providers',
    status: 'Live data',
  },
  {
    title: 'Platform settings',
    description: 'Review and update live platform configuration values.',
    to: '/staff/dashboard/platform-settings',
    status: 'Live data',
  },
  {
    title: 'Service categories',
    description:
      'Check category data and design empty states while seeding continues.',
    to: '/staff/dashboard/services/categories',
    status: 'Seed pending',
  },
  {
    title: 'HnB staff',
    description: 'Prepare internal operations screens for HnB personnel.',
    to: '/staff/dashboard/staff',
    status: 'Seed pending',
  },
  {
    title: 'AI plans',
    description: 'Stage future AI module configuration and plan management UX.',
    to: '/staff/dashboard/ai/plans',
    status: 'Future module',
  },
  {
    title: 'Provider plans',
    description: 'Preview subscription plan management for providers.',
    to: '/staff/dashboard/provider-plans',
    status: 'Seed pending',
  },
];

const summaryCards = [
  {
    label: 'Backend connection',
    value: 'Connected',
    tone: 'success' as const,
    helper: 'The dashboard is currently talking to the new HnB backend.',
  },
  {
    label: 'Live modules',
    value: '2',
    tone: 'default' as const,
    helper:
      'Providers and platform settings are already rendering backend-backed content.',
  },
  {
    label: 'Seed-pending modules',
    value: '3',
    tone: 'warning' as const,
    helper:
      'Staff, service categories, and provider plans are scaffolded and ready for data.',
  },
  {
    label: 'Future-phase modules',
    value: '1',
    tone: 'accent' as const,
    helper: 'AI plans is prepared as part of the later AI expansion path.',
  },
];

const nextSteps = [
  'Seed backend records for staff, service categories, and provider plans.',
  'Normalize fallback text and status vocabulary across seeded modules.',
  'Add richer list and detail flows where placeholder modules start receiving live data.',
  'Use this dashboard as the admin command surface for iterative ERP expansion.',
];

export default function DashboardHomePage() {
  return (
    <div className="dashboard-home">
      <style>
        {`
          .dashboard-home {
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          .dashboard-home__summary-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
          }

          .dashboard-home__content-grid {
            display: grid;
            grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
            gap: 16px;
            align-items: start;
          }

          .dashboard-home__module-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
          }

          .dashboard-home__module-card {
            text-decoration: none;
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            border: 1px solid ${adminTheme.color.border};
            border-radius: ${adminTheme.radius.md};
            padding: 18px;
            color: ${adminTheme.color.text};
            display: flex;
            flex-direction: column;
            gap: 12px;
            min-height: 180px;
            transition:
              transform 180ms ease,
              box-shadow 180ms ease,
              border-color 180ms ease,
              background 180ms ease;
          }

          .dashboard-home__module-card:hover {
            transform: translateY(-2px);
            box-shadow: ${adminTheme.shadow.md};
            border-color: ${adminTheme.color.borderStrong};
            background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%);
          }

          .dashboard-home__module-card:focus-visible {
            outline: 2px solid ${adminTheme.color.text};
            outline-offset: 3px;
          }

          .dashboard-home__module-top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
          }

          .dashboard-home__module-title {
            margin: 0;
            font-size: 1.05rem;
            line-height: 1.3;
            letter-spacing: -0.02em;
          }

          .dashboard-home__module-description {
            margin: 0;
            color: ${adminTheme.color.textMuted};
            line-height: 1.65;
            font-size: 0.95rem;
          }

          .dashboard-home__module-footer {
            margin-top: auto;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            color: ${adminTheme.color.text};
            font-weight: 700;
            font-size: 0.92rem;
          }

          .dashboard-home__steps {
            margin: 0;
            padding-left: 18px;
            color: ${adminTheme.color.textMuted};
            line-height: 1.7;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .dashboard-home__actions {
            border-top: 1px solid #f1f5f9;
            padding-top: 16px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .dashboard-home__section-note {
            margin: 0 0 16px 0;
            color: ${adminTheme.color.textMuted};
            line-height: 1.65;
            font-size: 0.95rem;
          }

          @media (max-width: 1180px) {
            .dashboard-home__summary-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .dashboard-home__content-grid {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 767px) {
            .dashboard-home {
              gap: 20px;
            }

            .dashboard-home__summary-grid {
              grid-template-columns: 1fr;
            }

            .dashboard-home__module-grid {
              grid-template-columns: 1fr;
            }

            .dashboard-home__module-card {
              min-height: auto;
              padding: 16px;
            }

            .dashboard-home__module-top {
              flex-direction: column;
              align-items: flex-start;
            }
          }
        `}
      </style>

      <PageIntro
        title="Dashboard"
        message="This admin surface is now acting as the control layer for the new HnB backend structure, with live modules, scaffolded modules, and future-phase areas prepared for continued integration."
      />

      <div className="dashboard-home__summary-grid">
        {summaryCards.map((card) => (
          <SummaryCard
            key={card.label}
            label={card.label}
            value={card.value}
            helper={card.helper}
            tone={card.tone}
          />
        ))}
      </div>

      <div className="dashboard-home__content-grid">
        <SectionCard
          eyebrow="Module navigation"
          title="Admin areas"
          message="Open a module below to continue data integration, validate seeded content, or refine admin workflows."
        >
          <p className="dashboard-home__section-note">
            These modules align with the role-based admin surface and staged ERP
            expansion path now being used across the rebuild.
          </p>

          <div className="dashboard-home__module-grid">
            {modules.map((card) => (
              <Link
                key={card.to}
                to={card.to}
                className="dashboard-home__module-card"
              >
                <div className="dashboard-home__module-top">
                  <h3 className="dashboard-home__module-title">{card.title}</h3>
                  <StatusPill label={card.status} />
                </div>

                <p className="dashboard-home__module-description">
                  {card.description}
                </p>

                <span className="dashboard-home__module-footer">
                  Open module
                  <span aria-hidden="true">→</span>
                </span>
              </Link>
            ))}
          </div>
        </SectionCard>

        <SectionCard eyebrow="Integration focus" title="Next steps">
          <ul className="dashboard-home__steps">
            {nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>

          <div className="dashboard-home__actions">
            <ActionLink to="/staff/dashboard/providers" label="Open providers" />
            <ActionLink
              to="/staff/dashboard/platform-settings"
              label="Open platform settings"
            />
            <ActionLink to="/staff/dashboard/staff" label="Open HnB staff" />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  helper,
  tone,
}: {
  label: string;
  value: string;
  helper: string;
  tone: 'default' | 'success' | 'warning' | 'accent';
}) {
  const toneStyles: Record<
    'default' | 'success' | 'warning' | 'accent',
    { background: string; text: string; border: string }
  > = {
    default: {
      background: adminTheme.color.surfaceMuted,
      text: adminTheme.color.text,
      border: adminTheme.color.border,
    },
    success: {
      background: adminTheme.color.successBg,
      text: adminTheme.color.successText,
      border: adminTheme.color.successBorder,
    },
    warning: {
      background: adminTheme.color.warningBg,
      text: adminTheme.color.warningText,
      border: adminTheme.color.warningBorder,
    },
    accent: {
      background: adminTheme.color.accentBg,
      text: adminTheme.color.accentText,
      border: adminTheme.color.accentBorder,
    },
  };

  const palette = toneStyles[tone];

  return (
    <div
      style={{
        background: adminTheme.color.surface,
        border: `1px solid ${adminTheme.color.border}`,
        borderRadius: adminTheme.radius.md,
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minHeight: '152px',
        boxShadow: adminTheme.shadow.sm,
      }}
    >
      <span
        style={{
          fontSize: '0.9rem',
          fontWeight: 600,
          color: adminTheme.color.textMuted,
        }}
      >
        {label}
      </span>

      <div
        style={{
          alignSelf: 'flex-start',
          padding: '8px 12px',
          borderRadius: adminTheme.radius.pill,
          background: palette.background,
          color: palette.text,
          border: `1px solid ${palette.border}`,
          fontWeight: 700,
          fontSize: '1rem',
        }}
      >
        {value}
      </div>

      <p
        style={{
          margin: 0,
          color: adminTheme.color.textMuted,
          lineHeight: 1.6,
        }}
      >
        {helper}
      </p>
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  if (label === 'Live data') {
    return <Pill label={label} />;
  }

  if (label === 'Future module') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '8px 12px',
          borderRadius: adminTheme.radius.pill,
          background: adminTheme.color.accentBg,
          color: adminTheme.color.accentText,
          border: `1px solid ${adminTheme.color.accentBorder}`,
          fontSize: '0.8rem',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: adminTheme.radius.pill,
        background: adminTheme.color.warningBg,
        color: adminTheme.color.warningText,
        border: `1px solid ${adminTheme.color.warningBorder}`,
        fontSize: '0.8rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}